import {
  Action,
  ActionType,
  BallAction,
  BasketballAction,
  BasketballActionType,
  FrameStencil,
  HandoffAction,
  HockeyActionType,
  Marker,
  MarkerBall,
  MarkerPlayer,
  MarkerType,
  Movement,
  PlayerAction,
  PlayerLayerType,
  PlayerReceivingHandoffAction,
  PlayerReceivingHandoffLayerType,
  PossessionAction,
  PossessionLayerType,
  StencilType,
} from "../../types";
import { SoccerActionLineType } from "../../types/frame-stencil/soccer";

/** ================================
 * Get Markers
 ================================ */
// Get stencils by type
export const getMarkerStencils = (stencils: FrameStencil[]): Marker[] =>
  stencils.filter((stencil): stencil is Marker => stencil.stencilType === StencilType.MARKER);

export interface GetMarkersWithLastMovementActionProps {
  stencils: FrameStencil[];
  action: Action;
}

export const getPlayerMarkers = (stencils: FrameStencil[]): MarkerPlayer[] =>
  getMarkerStencils(stencils).filter((stencil): stencil is MarkerPlayer => stencil.markerType === MarkerType.PLAYER);

export const getBallMarkers = (stencils: FrameStencil[]): MarkerBall[] =>
  getMarkerStencils(stencils).filter((stencil): stencil is MarkerBall => stencil.markerType === MarkerType.BALL);

export const getPlayerMarkersNoMovement = (stencils: FrameStencil[]): MarkerPlayer[] =>
  getPlayerMarkers(stencils).filter((marker) => marker.movements.length === 0);

export const getBallMarkersNoMovement = (stencils: FrameStencil[]): MarkerBall[] =>
  getBallMarkers(stencils).filter((marker) => marker.movements.length === 0);

export const getMarkersWithLastMovementAction = ({ stencils, action }: GetMarkersWithLastMovementActionProps) => {
  return [...getPlayerMarkers(stencils), ...getBallMarkers(stencils)].filter(
    (s) => s.movements[s.movements.length - 1]?.id === action.id
  );
};

export interface GetBallActionsNextPlayerProps {
  stencils: FrameStencil[];
  action: BallAction;
}
export const getBallActionsNextPlayer = ({ stencils, action }: GetBallActionsNextPlayerProps): MarkerPlayer | null =>
  getPlayerMarkers(stencils).find((player) => player.id === action.nextPlayer) || null;

export interface GetPlayerOnActionProps {
  stencils: FrameStencil[];
  action: Action;
}

export const findPlayerOnAction = ({ stencils, action }: GetPlayerOnActionProps): MarkerPlayer | null => {
  return getPlayerMarkers(stencils).find((s) => s.movements.map((m) => m.id).includes(action.id)) || null;
};

/** ================================
 * Get Actions
 ================================ */
export const getActionStencils = (stencils: FrameStencil[]): Action[] =>
  stencils.filter((stencil): stencil is Action => stencil.stencilType === StencilType.ACTION);

export const getPlayerActions = (stencils: FrameStencil[]): PlayerAction[] =>
  getActionStencils(stencils).filter((stencil): stencil is PlayerAction => stencil.actionType === ActionType.PLAYER);

export const getBallActions = (stencils: FrameStencil[]): BallAction[] =>
  getActionStencils(stencils).filter((stencil): stencil is BallAction => stencil.actionType === ActionType.BALL);

export const getHandoffActions = (stencils: FrameStencil[]): HandoffAction[] =>
  getActionStencils(stencils).filter((stencil): stencil is HandoffAction => stencil.actionType === ActionType.HANDOFF);

export const getPossessionActions = (stencils: FrameStencil[]): PossessionAction[] =>
  getActionStencils(stencils).filter(
    (stencil): stencil is PossessionAction => stencil.actionType === ActionType.POSSESSION
  );

export const getPlayerAndPossessionActions = (stencils: FrameStencil[]): (PlayerAction | PossessionAction)[] => [
  ...getPlayerActions(stencils),
  ...getPossessionActions(stencils),
];

export const getBallAndPossessionActions = (stencils: FrameStencil[]): (BallAction | PossessionAction)[] => [
  ...getBallActions(stencils),
  ...getPossessionActions(stencils),
];

// Get stencils without nexts
export const getPlayerActionsNoNext = (stencils: FrameStencil[]): PlayerAction[] =>
  getPlayerActions(stencils).filter((action) => action.nextPlayerAction === null);

export const getBallActionsNoNext = (stencils: FrameStencil[]): BallAction[] =>
  getBallActions(stencils).filter((action) => action.nextBallAction === null);

export const getBallActionsNoNextPlayer = (stencils: FrameStencil[]): BallAction[] =>
  getBallActions(stencils).filter((action) => action.nextPlayer === null);

export const getPossessionActionsNoNextPlayer = (stencils: FrameStencil[]): PossessionAction[] =>
  getPossessionActions(stencils).filter((action) => action.nextPlayerAction === null);

export const getPossessionActionsNoNextBall = (stencils: FrameStencil[]): PossessionAction[] =>
  getPossessionActions(stencils).filter((action) => action.nextBallAction === null);

export const getHandoffsNoNextAction = (stencils: FrameStencil[]): HandoffAction[] =>
  getHandoffActions(stencils).filter((action) => action.nextAction === null);

/** get all actions that are not in another action's next or a marker's movement */
export const getNonAssociatedActions = (
  stencils: FrameStencil[],
  markerTypeCheck?: MarkerType.PLAYER | MarkerType.BALL
): Action[] => {
  const actions = getActionStencils(stencils);
  const dynamicMarkers =
    markerTypeCheck === MarkerType.PLAYER
      ? getPlayerMarkers(stencils)
      : markerTypeCheck === MarkerType.BALL
      ? getBallMarkers(stencils)
      : [...getPlayerMarkers(stencils), ...getBallMarkers(stencils)];

  const actionsNextIds = actions.flatMap((action) => {
    const { actionType } = action;
    const nextIds = [];
    if ((actionType === ActionType.PLAYER || actionType === ActionType.POSSESSION) && action.nextPlayerAction)
      nextIds.push(action.nextPlayerAction);
    if ((actionType === ActionType.BALL || actionType === ActionType.POSSESSION) && action.nextBallAction)
      nextIds.push(action.nextBallAction);

    return nextIds;
  });

  const movementIds = dynamicMarkers.flatMap((marker) => marker.movements.map((movement) => movement.id));

  return actions.filter((action) => ![...actionsNextIds, ...movementIds].includes(action.id));
};

export const getNonAssociatedPlayerAndPossessionActions = (
  stencils: FrameStencil[],
  checkOnlyPlayerMarkers?: boolean
): (PlayerAction | PossessionAction)[] => {
  const nonAssociatedActions = getNonAssociatedActions(
    stencils,
    checkOnlyPlayerMarkers ? MarkerType.PLAYER : undefined
  );
  return [...getPlayerActions(nonAssociatedActions), ...getPossessionActions(nonAssociatedActions)];
};

export const getNonAssociatedBallAndPossessionActions = (
  stencils: FrameStencil[],
  checkOnlyBallMarkers?: boolean
): (BallAction | PossessionAction)[] => {
  const nonAssociatedActions = getNonAssociatedActions(stencils, checkOnlyBallMarkers ? MarkerType.BALL : undefined);
  return [...getBallActions(nonAssociatedActions), ...getPossessionActions(nonAssociatedActions)];
};

export const getNonAssociatedHandoffActions = (stencils: FrameStencil[]): HandoffAction[] =>
  getNonAssociatedActions(stencils).filter(
    (action): action is HandoffAction => action.actionType === ActionType.HANDOFF
  );

export interface GetPlayersNextPassProps {
  stencils: FrameStencil[];
  player: MarkerPlayer;
}

export const getMovements = (stencils: FrameStencil[]): Movement[] =>
  [...getPlayerMarkers(stencils), ...getBallMarkers(stencils)].flatMap((marker) => marker.movements);

export const getAllPlayerMovementActions = (stencils: FrameStencil[]): Action[] => {
  const actions = getActionStencils(stencils);

  return getPlayerMarkers(stencils).flatMap((marker) =>
    marker.movements
      .map((movement) => actions.find((action) => action.id === movement.id))
      .filter((a): a is Action => a !== undefined)
  );
};
export const getAllBallMovementActions = (stencils: FrameStencil[]): Action[] => {
  const actions = getActionStencils(stencils);

  return getBallMarkers(stencils).flatMap((marker) =>
    marker.movements
      .map((movement) => actions.find((action) => action.id === movement.id))
      .filter((a): a is Action => a !== undefined)
  );
};

export const getAllMovementActions = (stencils: FrameStencil[]): Action[] => [
  ...getAllPlayerMovementActions(stencils),
  ...getAllBallMovementActions(stencils),
];

export interface GetActionsFromMovementsProps {
  movements: Movement[];
  stencils: FrameStencil[];
}

export const getActionsFromMovements = ({ movements, stencils }: GetActionsFromMovementsProps): Action[] => {
  const actions = getActionStencils(stencils);

  return movements
    .map((movement) => actions.find((action) => action.id === movement.id))
    .filter((a): a is Action => a !== undefined);
};

export const getReceivingHandoffLayerType = (prevActionType: PlayerLayerType): PossessionLayerType => {
  switch (prevActionType) {
    case BasketballActionType.CUT:
      return BasketballActionType.CUT_RECEIVING_HANDOFF;
    case BasketballActionType.SCREEN:
      return BasketballActionType.SCREEN_RECEIVING_HANDOFF;

    case HockeyActionType.SKATE:
      return HockeyActionType.SKATE_RECEIVING_HANDOFF;
    case HockeyActionType.SKATE_BACKWARDS:
      return HockeyActionType.SKATE_BACKWARDS_RECEIVING_HANDOFF;
    case HockeyActionType.SKATE_LATERAL:
      return HockeyActionType.SKATE_LATERAL_RECEIVING_HANDOFF;

    case SoccerActionLineType.SC_RUN:
      return SoccerActionLineType.SC_RUN_RECEIVING_HANDOFF;
    case SoccerActionLineType.SC_RUN_AND_STOP:
      return SoccerActionLineType.SC_RUN_AND_STOP_RECEIVING_HANDOFF;
  }
};

export const getPlayerActionFromReceivingHandoffLayer = (
  prevActionType: PlayerReceivingHandoffLayerType
): PlayerLayerType => {
  switch (prevActionType) {
    case BasketballActionType.CUT_RECEIVING_HANDOFF:
      return BasketballActionType.CUT;
    case BasketballActionType.SCREEN_RECEIVING_HANDOFF:
      return BasketballActionType.SCREEN;

    case HockeyActionType.SKATE_RECEIVING_HANDOFF:
      return HockeyActionType.SKATE;
    case HockeyActionType.SKATE_BACKWARDS_RECEIVING_HANDOFF:
      return HockeyActionType.SKATE_BACKWARDS;
    case HockeyActionType.SKATE_LATERAL_RECEIVING_HANDOFF:
      return HockeyActionType.SKATE_LATERAL;

    case SoccerActionLineType.SC_RUN_RECEIVING_HANDOFF:
      return SoccerActionLineType.SC_RUN;
    case SoccerActionLineType.SC_RUN_AND_STOP_RECEIVING_HANDOFF:
      return SoccerActionLineType.SC_RUN_AND_STOP;
  }
};

/** ================================
 * Check Stencil Types
 ================================ */
export const isPlayer = (stencil: FrameStencil): stencil is MarkerPlayer =>
  stencil.stencilType === StencilType.MARKER && stencil.markerType === MarkerType.PLAYER;

export const isPlayerAction = (action: Action): action is PlayerAction => action.actionType === ActionType.PLAYER;
export const isBallAction = (action: Action): action is BallAction => action.actionType === ActionType.BALL;
export const isPossessionAction = (action: Action): action is PossessionAction =>
  action.actionType === ActionType.POSSESSION;
export const isHandoffAction = (action: Action): action is HandoffAction => action.actionType === ActionType.HANDOFF;
export const isPlayerOrPossessionAction = (action: Action): action is PlayerAction | PossessionAction =>
  isPlayerAction(action) || isPossessionAction(action);
export const isBallOrPossessionAction = (action: Action): action is BallAction | PossessionAction =>
  isBallAction(action) || isPossessionAction(action);

export const isAnimateableStencil = (stencil: FrameStencil): stencil is MarkerPlayer | MarkerBall =>
  stencil.stencilType === StencilType.MARKER &&
  (stencil.markerType === MarkerType.PLAYER || stencil.markerType === MarkerType.BALL);

export const isBasketballAction = (action: Partial<Action>): action is BasketballAction =>
  action.layerType === BasketballActionType.CUT ||
  action.layerType === BasketballActionType.DRIBBLE ||
  action.layerType === BasketballActionType.HANDOFF ||
  action.layerType === BasketballActionType.HASHPASS ||
  action.layerType === BasketballActionType.PASS ||
  action.layerType === BasketballActionType.SCREEN;

export const isReceivingHandoffLayer = (action: PossessionAction): action is PlayerReceivingHandoffAction =>
  action.layerType === BasketballActionType.CUT_RECEIVING_HANDOFF ||
  action.layerType === BasketballActionType.SCREEN_RECEIVING_HANDOFF ||
  action.layerType === HockeyActionType.SKATE_RECEIVING_HANDOFF ||
  action.layerType === HockeyActionType.SKATE_BACKWARDS_RECEIVING_HANDOFF ||
  action.layerType === HockeyActionType.SKATE_LATERAL_RECEIVING_HANDOFF ||
  action.layerType === SoccerActionLineType.SC_RUN_RECEIVING_HANDOFF ||
  action.layerType === SoccerActionLineType.SC_RUN_AND_STOP_RECEIVING_HANDOFF;
